import { useState } from "react";
import { PencilLine } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export type Alteracao = {
  id: string;
  serviceId: string;
  campo: string;
  rotuloAntes: string | null;
  rotuloDepois: string | null;
  createdAt: string | Date;
  user: { name: string | null } | null;
};

const NOME_DO_CAMPO: Record<string, string> = {
  serviceCategoryId: "Serviço",
  materialOptionId: "Material",
  materialCategoryId: "Material",
  shadeOptionId: "Cor",
  teeth: "Dentes",
  quantidade: "Unidades",
  notes: "Observações",
};

const VISIVEIS = 2;

/** Uma edição do laboratório = as linhas gravadas juntas (mesmo instante, mesma pessoa). */
function agrupar(alteracoes: Alteracao[]) {
  const grupos: { chave: string; quando: Date; quem: string | null; itens: Alteracao[] }[] = [];
  for (const a of alteracoes) {
    // Troca de oferta com o mesmo nome (ex.: cor A3 do molde → A3 do laboratório) não é mudança
    // que o dentista enxergue.
    if ((a.rotuloAntes ?? "") === (a.rotuloDepois ?? "")) continue;
    const quando = new Date(a.createdAt);
    const chave = `${quando.getTime()}-${a.user?.name ?? ""}`;
    const ultimo = grupos.at(-1);
    if (ultimo?.chave === chave) ultimo.itens.push(a);
    else grupos.push({ chave, quando, quem: a.user?.name ?? null, itens: [a] });
  }
  return grupos;
}

/** O que o laboratório mudou no serviço depois de receber o pedido — antes → depois, quando e quem. */
export function AlteracoesDoServico({ alteracoes }: { alteracoes: Alteracao[] }) {
  const [todas, setTodas] = useState(false);
  const grupos = agrupar(alteracoes);
  if (grupos.length === 0) return null;
  const mostrados = todas ? grupos : grupos.slice(0, VISIVEIS);

  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-500/30 dark:bg-amber-500/10">
      <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
        <PencilLine className="h-3.5 w-3.5" />
        Alterado pelo laboratório
      </p>
      <div className="space-y-2">
        {mostrados.map((g) => (
          <div key={g.chave}>
            <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
              {format(g.quando, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              {g.quem ? ` · ${g.quem}` : ""}
            </p>
            <ul className="mt-0.5 space-y-0.5">
              {g.itens.map((a) => (
                <li key={a.id} className="text-[12px] text-zinc-700 dark:text-foreground/90">
                  <span className="font-semibold">{NOME_DO_CAMPO[a.campo] ?? a.campo}:</span>{" "}
                  <span className="text-zinc-500 line-through dark:text-muted-foreground">{a.rotuloAntes || "vazio"}</span>
                  {" → "}
                  <span className="font-medium">{a.rotuloDepois || "vazio"}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {grupos.length > VISIVEIS && (
        <button
          type="button"
          onClick={() => setTodas((v) => !v)}
          className="mt-2 text-[12px] font-semibold text-amber-700 hover:underline dark:text-amber-400"
        >
          {todas ? "Ver menos" : `Ver todas (${grupos.length})`}
        </button>
      )}
    </div>
  );
}
